import { useCallback, useMemo } from 'react';

import { useCoverageConsentConfirmation } from './useCoverageConsentConfirmation';

interface UseCoverageDeclineConfirmationOptions {
  quoteId: number;
  vehicleVin: string;
  vehicleId: number;
  consentType: number;
  currentEnabled: boolean;
  onApplyChange: (enabled: boolean) => void;
}

export const useCoverageDeclineConfirmation = ({
  quoteId,
  vehicleVin,
  vehicleId,
  consentType,
  currentEnabled,
  onApplyChange,
}: UseCoverageDeclineConfirmationOptions) => {
  const handleApply = useCallback(
    (newValue: string) => {
      onApplyChange(newValue === 'true');
    },
    [onApplyChange],
  );

  const {
    requestConfirmation,
    applyChangeDirectly,
    resetState,
    modalProps: baseModalProps,
  } = useCoverageConsentConfirmation({
    quoteId,
    vehicleVin,
    vehicleId,
    consentType,
    onApplyChange: handleApply,
    shouldClearOther: () => false,
  });

  const handleChange = useCallback(
    (newEnabled: boolean) => {
      const wasEnabled = currentEnabled;
      const isDisabling = !newEnabled;

      if (wasEnabled && isDisabling) {
        requestConfirmation('false', 'true');
      } else {
        applyChangeDirectly(String(newEnabled));
      }
    },
    [currentEnabled, requestConfirmation, applyChangeDirectly],
  );

  const modalProps = useMemo(
    () => ({
      isOpen: baseModalProps.isOpen,
      onClose: baseModalProps.onClose,
      onConfirm: baseModalProps.onConfirm,
      isLoading: baseModalProps.isLoading,
      consentType,
    }),
    [baseModalProps, consentType],
  );

  return {
    handleChange,
    resetState,
    modalProps,
  };
};
